import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCancelRegistration, useRegistrations, useTournament } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Loader, ErrorView } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { BlueHeader, HeaderBack } from '../components/ScreenShell';
import { backButton, haptics } from '../telegram';
import { track } from '../analytics';
import { shortDate, timeLabel } from '../format';

/** "Записались" — tournament lobby: who registered, with rank. */
export function RegisteredPlayersScreen() {
  const { id } = useParams();
  const tid = Number(id);
  const navigate = useNavigate();
  const { player } = useAuth();
  const { data: t, isLoading, isError, error } = useTournament(tid);
  const regs = useRegistrations(tid);
  const cancel = useCancelRegistration(tid);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (isLoading) return <Loader />;
  if (isError || !t) return <ErrorView message={(error as Error)?.message} />;

  const cap = t.capacity ?? 0;
  const free = cap > 0 ? Math.max(0, cap - (t.registeredCount - (t.bustedCount ?? 0))) : 0;
  const all = regs.data?.players ?? [];
  const rows = all.filter((r) => !r.waitlisted);
  const waitlist = all.filter((r) => r.waitlisted);

  const doCancel = async () => {
    try {
      await cancel.mutateAsync();
      haptics.success();
    } catch {
      haptics.error();
    }
  };

  return (
    <div>
      <BlueHeader wave={false} mascot="tr">
        <div className="bhead-top" style={{ marginBottom: 14 }}>
          <HeaderBack onClick={() => navigate(-1)} />
          <div className="bhead-title bhead-title--sm" style={{ flex: 1 }}>
            Записались
          </div>
        </div>
        <div className="bhead-meta" style={{ marginTop: 0 }}>
          {t.title ?? t.format ?? 'Турнир'} · {shortDate(t.startTime)} · {timeLabel(t.startTime)}
        </div>
        <div className="bhead-stats">
          <div className="bhead-stat">
            <div className="v">{t.registeredCount}</div>
            <div className="l">записались</div>
          </div>
          {cap > 0 && (
            <>
              <div className="bhead-stat">
                <div className="v">{cap}</div>
                <div className="l">мест всего</div>
              </div>
              <div className="bhead-stat">
                <div className="v ok">{free}</div>
                <div className="l">свободно</div>
              </div>
            </>
          )}
        </div>
      </BlueHeader>

      <div className="content">
      {regs.isLoading ? (
        <Loader />
      ) : rows.length === 0 && waitlist.length === 0 ? (
        <div className="muted tiny">Пока никто не записался</div>
      ) : (
        <div>
          {rows.map((r) => {
            const me = player?.id === r.playerId;
            return (
              <Link
                className={`lobby-row ${me ? 'me' : ''}`}
                key={r.playerId}
                to={`/players/${r.playerId}`}
                onClick={() => track('player_open', { playerId: r.playerId, from: 'registrations' })}
              >
                <Avatar name={r.nickname} src={r.photoUrl} size={38} solid={me} />
                <div className="info">
                  <div className="nm">{me ? `Вы · ${r.nickname}` : r.nickname}</div>
                  {me && <div className="sub">Вы записаны</div>}
                </div>
                {r.rank ? <span className="rank-pill">#{r.rank}</span> : null}
              </Link>
            );
          })}

          {waitlist.length > 0 && (
            <>
              <div className="date-group" style={{ marginTop: 16 }}>
                Лист ожидания · {waitlist.length}
              </div>
              {waitlist.map((r, i) => {
                const me = player?.id === r.playerId;
                return (
                  <Link
                    className={`lobby-row ${me ? 'me' : ''}`}
                    key={r.playerId}
                    to={`/players/${r.playerId}`}
                    style={{ opacity: 0.75 }}
                    onClick={() => track('player_open', { playerId: r.playerId, from: 'registrations' })}
                  >
                    <Avatar name={r.nickname} src={r.photoUrl} size={38} solid={me} />
                    <div className="info">
                      <div className="nm">{me ? `Вы · ${r.nickname}` : r.nickname}</div>
                      <div className="sub">№{i + 1} в очереди</div>
                    </div>
                    {r.rank ? <span className="rank-pill">#{r.rank}</span> : null}
                  </Link>
                );
              })}
            </>
          )}
        </div>
      )}

      {t.registeredByMe && (
        <>
          <div style={{ height: 88 }} />
          <div className="detail-footer">
            <div className="fbuyin">
              <div
                className="v"
                style={{
                  color: t.myWaitlisted ? 'var(--brand-text)' : 'var(--success)',
                  fontSize: 13,
                  fontFamily: 'var(--font-ui)',
                  fontWeight: 700,
                }}
              >
                {t.myWaitlisted ? 'Лист ожидания' : '✓ Вы записаны'}
              </div>
              <div className="l">
                {t.myWaitlisted
                  ? t.myWaitlistPosition
                    ? `№${t.myWaitlistPosition} в очереди`
                    : 'ждём место'
                  : 'место за вами'}
              </div>
            </div>
            <button className="btn btn--danger" onClick={doCancel} disabled={cancel.isPending}>
              {cancel.isPending ? 'Отмена…' : t.myWaitlisted ? 'Выйти из листа' : 'Отменить запись'}
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
